import { ApiSort } from "@result/rui/utils/api";

export default class DataLayerSort {
  #api = null;

  #func = null;

  /**
   * Constructor for the DataLayerSort class.
   *
   * @param {ApiSort} api - API instance to use for fetching data
   * @param {function} func - Optional function to apply additional client-side sorting to the results from the service. It should take two items and return a negative number if the first item should come before the second, a positive number if the first item should come after the second, or zero if they are considered equal in terms of sorting order.
   */
  constructor(api, func) {
    this.#api = new ApiSort().from(api);
    this.#func = func ?? convertToFunc(this.#api);
  }

  get api() {
    return this.#api;
  }

  get func() {
    return this.#func;
  }
}

/**
 * Extract the API sort from param
 *
 * @param {DataLayerSort|ApiSort|function} s
 * @returns {ApiSort|null}
 */
export function apiSort(s) {
  if (typeof s === "function") {
    // eslint-disable-next-line no-param-reassign
    s = s();
  }

  if (s && s instanceof DataLayerSort) {
    return s.api;
  }

  if (s && typeof s === "object" && "directionOf" in s) {
    return s;
  }

  if (s && typeof s === "object" && typeof s === "string") {
    return new ApiSort().from(s); // validate the sort object
  }

  if (s === null || s === undefined) {
    return null;
  }

  // eslint-disable-next-line no-console
  console.error("Invalid sort provided to apiSort:", s);
  return null;
}

/**
 * Extract the sorting function from param
 *
 * @param {DataLayerSort|function} s
 * @returns
 */
export function funcSort(s) {
  if (!s) return null;

  if (s instanceof DataLayerSort) {
    return s.func;
  }

  if (typeof s === "function") {
    return s;
  }

  if (typeof s === "object" && "directionOf" in s) {
    return convertToFunc(s);
  }

  // eslint-disable-next-line no-console
  console.error("Invalid sort function provided to funcSort:", s);
  return null;
}

// converts an ApiSort object to a sorting function that can be applied on the client side
export function convertToFunc(sort) {
  if (!sort) return () => 0;

  if (!(sort instanceof ApiSort)) {
    // eslint-disable-next-line no-console
    throw new Error("Invalid sort provided to convertToFunc:", sort);
  }

  return (a, b) => {
    // @todo this might conver some cases (null values, nested)
    for (const { property, direction } of sort.orders) {
      const aValue = a[property];
      const bValue = b[property];

      if (aValue < bValue) {
        return direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === "asc" ? 1 : -1;
      }
    }

    return 0; // consider equal if all properties are equal
  };
}
